import {
    OutreachStatusFilter,
    RecruitingStatusFilter,
    SystemStatusFilter,
} from '__generated__/graphql'
import { codec } from './useUrlState'

export type SearchFilterItem = {
    rosterId?: string
    processStatusId?: string
    processStatusGroupId?: string
}

const searchFilterArrayCodec = {
    default: [] as SearchFilterItem[],
    parse: (raw: string): SearchFilterItem[] => {
        if (!raw) return []
        return raw.split('|').map((item) => {
            const [first, second] = item.split('~').map(decodeURIComponent)
            if (first.startsWith('g:')) {
                return { processStatusGroupId: first.slice(2) }
            }
            if (first.startsWith('s:')) {
                return { processStatusId: first.slice(2) }
            }
            if (second?.startsWith('g:')) {
                return {
                    rosterId: first,
                    processStatusGroupId: second.slice(2),
                }
            }
            return second
                ? { rosterId: first, processStatusId: second }
                : { rosterId: first }
        })
    },
    serialize: (val: SearchFilterItem[]): string | null =>
        val.length > 0
            ? val
                  .map((item) => {
                      if (item.rosterId && item.processStatusId) {
                          return `${encodeURIComponent(item.rosterId)}~${encodeURIComponent(item.processStatusId)}`
                      }
                      if (item.rosterId && item.processStatusGroupId) {
                          return `${encodeURIComponent(item.rosterId)}~g:${encodeURIComponent(item.processStatusGroupId)}`
                      }
                      if (item.rosterId) {
                          return encodeURIComponent(item.rosterId)
                      }
                      if (item.processStatusGroupId) {
                          return `g:${encodeURIComponent(item.processStatusGroupId)}`
                      }
                      if (item.processStatusId) {
                          return `s:${encodeURIComponent(item.processStatusId)}`
                      }
                      return ''
                  })
                  .filter(Boolean)
                  .join('|')
            : null,
    urlKey: 'searchFilters',
}

export type CandidateFilterState = {
    quickSearch: string | undefined
    colorNames: string[]
    name: string | undefined
    recruitingStatus: RecruitingStatusFilter[]
    outreachStatus: OutreachStatusFilter[]
    systemStatus: SystemStatusFilter[]
    starRatingMin: number | undefined
    starRatingMax: number | undefined
    screeningNoteTemplateIds: string[]
    screeningNoteStarRatingMin: number | undefined
    screeningNoteStarRatingMax: number | undefined
    companyIds: string[]
    companyTimeFrame: string | undefined
    companyRangeGte: string | undefined
    companyRangeLte: string | undefined
    companyAdvancedStartGte: string | undefined
    companyAdvancedStartLte: string | undefined
    companyAdvancedEndGte: string | undefined
    companyAdvancedEndLte: string | undefined
    sectorNames: string[]
    sectorNamesNot: string[]
    sectorTimeFrame: string | undefined
    sectorRangeGte: string | undefined
    sectorRangeLte: string | undefined
    sectorTopTierOnly: boolean
    industryNames: string[]
    industryNamesNot: string[]
    industryTimeFrame: string | undefined
    industryRangeGte: string | undefined
    industryRangeLte: string | undefined
    industryTopTierOnly: boolean
    positionTitles: string[]
    positionTimeFrame: string | undefined
    positionRangeGte: string | undefined
    positionRangeLte: string | undefined
    locationIds: string[]
    locationTimeFrame: string | undefined
    locationRangeGte: string | undefined
    locationRangeLte: string | undefined
    locationIncludeStateCountry: boolean
    locationText: string[]
    locationTextTimeFrame: string | undefined
    locationTextRangeGte: string | undefined
    locationTextRangeLte: string | undefined
    analystClassYearGte: number | undefined
    analystClassYearLte: number | undefined
    searchFilters: SearchFilterItem[]
    genderIds: string[]
    ethnicityIds: string[]
    hometownSearchTerms: string[]
    citizenshipIds: string[]
    visaStatusIds: string[]
    visaStatusIdsNot: string[]
    qualitativeIds: string[]
    qualitativeIdsNot: string[]
    revenueExperienceIds: string[]
    lgbtqIds: string[]
    cpiPlacedIds: string[]
    cpiPlacedIdsNot: string[]
    referenceAttributeIds: string[]
    mbaTierIds: string[]
    industryPrefFirst: string[]
    industryPrefSecond: string[]
    industryPrefThird: string[]
    sectorPrefFirst: string[]
    sectorPrefSecond: string[]
    sectorPrefThird: string[]
    companyPrefFirst: string[]
    companyPrefSecond: string[]
    locationPrefFirst: string[]
    locationPrefSecond: string[]
    locationPrefThird: string[]
    undergradCollegeIds: string[]
    mbaCollegeIds: string[]
    undergradCollegeNames: string[]
    graduateCollegeNames: string[]
    majorIds: string[]
    degreeTypeIds: string[]
    degreeTypeIdsNot: string[]
    gpaRangeMin: number | undefined
    gpaRangeMax: number | undefined
    gpaNoGpaMode: 'include' | 'exclude' | 'only' | undefined
    graduationRangeGte: number | undefined
    graduationRangeLte: number | undefined
    graduationEducationType: string | undefined
    startDateGte: string | undefined
    startDateLte: string | undefined
    prefStartDates: string[]
    processStatusGroup: string | undefined
    processStatusIds: string[]
    processStatusGroupIds: string[]
    remeetFilter: string | undefined
    includeDeactivated: boolean
}

export const filterCodec = {
    quickSearch: codec.string({ urlKey: 'q' }),
    colorNames: codec.stringArray({ urlKey: 'color' }),
    name: codec.string(),
    recruitingStatus: codec.stringArray({ urlKey: 'recruiting' }),
    outreachStatus: codec.stringArray({ urlKey: 'outreach' }),
    systemStatus: codec.stringArray({ urlKey: 'sysStatus' }),
    starRatingMin: codec.float({ urlKey: 'starFrom' }),
    starRatingMax: codec.float({ urlKey: 'starTo' }),
    screeningNoteTemplateIds: codec.stringArray({ urlKey: 'screenTemplate' }),
    screeningNoteStarRatingMin: codec.float({ urlKey: 'screenStarFrom' }),
    screeningNoteStarRatingMax: codec.float({ urlKey: 'screenStarTo' }),
    companyIds: codec.stringArray({ urlKey: 'company' }),
    companyTimeFrame: codec.string({ urlKey: 'companyTime' }),
    companyRangeGte: codec.string({ urlKey: 'companyFrom' }),
    companyRangeLte: codec.string({ urlKey: 'companyTo' }),
    companyAdvancedStartGte: codec.string({ urlKey: 'compStartGte' }),
    companyAdvancedStartLte: codec.string({ urlKey: 'compStartLte' }),
    companyAdvancedEndGte: codec.string({ urlKey: 'compEndGte' }),
    companyAdvancedEndLte: codec.string({ urlKey: 'compEndLte' }),
    sectorNames: codec.stringArray({ urlKey: 'sector' }),
    sectorNamesNot: codec.stringArray({ urlKey: 'sectorNot' }),
    sectorTimeFrame: codec.string({ urlKey: 'sectorTime' }),
    sectorRangeGte: codec.string({ urlKey: 'sectorFrom' }),
    sectorRangeLte: codec.string({ urlKey: 'sectorTo' }),
    sectorTopTierOnly: codec.boolean({ urlKey: 'sectorTopTier' }),
    industryNames: codec.stringArray({ urlKey: 'industry' }),
    industryNamesNot: codec.stringArray({ urlKey: 'industryNot' }),
    industryTimeFrame: codec.string({ urlKey: 'industryTime' }),
    industryRangeGte: codec.string({ urlKey: 'industryFrom' }),
    industryRangeLte: codec.string({ urlKey: 'industryTo' }),
    industryTopTierOnly: codec.boolean({ urlKey: 'industryTopTier' }),
    positionTitles: codec.stringArray({ urlKey: 'position' }),
    positionTimeFrame: codec.string({ urlKey: 'positionTime' }),
    positionRangeGte: codec.string({ urlKey: 'positionFrom' }),
    positionRangeLte: codec.string({ urlKey: 'positionTo' }),
    locationIds: codec.stringArray({ urlKey: 'location' }),
    locationTimeFrame: codec.string({ urlKey: 'locationTime' }),
    locationRangeGte: codec.string({ urlKey: 'locationFrom' }),
    locationRangeLte: codec.string({ urlKey: 'locationTo' }),
    locationIncludeStateCountry: codec.boolean({ urlKey: 'locationState' }),
    locationText: codec.stringArray({ urlKey: 'locationText' }),
    locationTextTimeFrame: codec.string({ urlKey: 'locTextTime' }),
    locationTextRangeGte: codec.string({ urlKey: 'locTextFrom' }),
    locationTextRangeLte: codec.string({ urlKey: 'locTextTo' }),
    analystClassYearGte: codec.int({ urlKey: 'classYearFrom' }),
    analystClassYearLte: codec.int({ urlKey: 'classYearTo' }),
    searchFilters: searchFilterArrayCodec,
    genderIds: codec.stringArray({ urlKey: 'gender' }),
    ethnicityIds: codec.stringArray({ urlKey: 'ethnicity' }),
    hometownSearchTerms: codec.stringArray({ urlKey: 'hometown' }),
    citizenshipIds: codec.stringArray({ urlKey: 'citizenship' }),
    visaStatusIds: codec.stringArray({ urlKey: 'visa' }),
    visaStatusIdsNot: codec.stringArray({ urlKey: 'visaNot' }),
    qualitativeIds: codec.stringArray({ urlKey: 'qualitative' }),
    qualitativeIdsNot: codec.stringArray({ urlKey: 'qualitativeNot' }),
    revenueExperienceIds: codec.stringArray({ urlKey: 'revenue' }),
    lgbtqIds: codec.stringArray({ urlKey: 'lgbtq' }),
    cpiPlacedIds: codec.stringArray({ urlKey: 'cpiPlaced' }),
    cpiPlacedIdsNot: codec.stringArray({ urlKey: 'cpiPlacedNot' }),
    referenceAttributeIds: codec.stringArray({ urlKey: 'refAttribute' }),
    mbaTierIds: codec.stringArray({ urlKey: 'mbaTier' }),
    industryPrefFirst: codec.stringArray({ urlKey: 'indPref1' }),
    industryPrefSecond: codec.stringArray({ urlKey: 'indPref2' }),
    industryPrefThird: codec.stringArray({ urlKey: 'indPref3' }),
    sectorPrefFirst: codec.stringArray({ urlKey: 'secPref1' }),
    sectorPrefSecond: codec.stringArray({ urlKey: 'secPref2' }),
    sectorPrefThird: codec.stringArray({ urlKey: 'secPref3' }),
    companyPrefFirst: codec.stringArray({ urlKey: 'comPref1' }),
    companyPrefSecond: codec.stringArray({ urlKey: 'comPref2' }),
    locationPrefFirst: codec.stringArray({ urlKey: 'locPref1' }),
    locationPrefSecond: codec.stringArray({ urlKey: 'locPref2' }),
    locationPrefThird: codec.stringArray({ urlKey: 'locPref3' }),
    undergradCollegeIds: codec.stringArray({ urlKey: 'undergradCollege' }),
    mbaCollegeIds: codec.stringArray({ urlKey: 'mbaCollege' }),
    undergradCollegeNames: codec.stringArray({
        urlKey: 'undergradCollegeName',
    }),
    graduateCollegeNames: codec.stringArray({ urlKey: 'gradCollegeName' }),
    majorIds: codec.stringArray({ urlKey: 'major' }),
    degreeTypeIds: codec.stringArray({ urlKey: 'degree' }),
    degreeTypeIdsNot: codec.stringArray({ urlKey: 'degreeNot' }),
    gpaRangeMin: codec.float({ urlKey: 'gpaFrom' }),
    gpaRangeMax: codec.float({ urlKey: 'gpaTo' }),
    gpaNoGpaMode: codec.string({ urlKey: 'gpaNoGpa' }),
    graduationRangeGte: codec.int({ urlKey: 'gradFrom' }),
    graduationRangeLte: codec.int({ urlKey: 'gradTo' }),
    graduationEducationType: codec.string({ urlKey: 'gradType' }),
    startDateGte: codec.string({ urlKey: 'startFrom' }),
    startDateLte: codec.string({ urlKey: 'startTo' }),
    prefStartDates: codec.stringArray({ urlKey: 'prefStart' }),
    processStatusGroup: codec.string({ urlKey: 'statusGroup' }),
    processStatusIds: codec.stringArray({ urlKey: 'processStatus' }),
    processStatusGroupIds: codec.stringArray({ urlKey: 'processStatusGroup' }),
    remeetFilter: codec.string({ urlKey: 'remeet' }),
    includeDeactivated: codec.boolean({ urlKey: 'incDeactivated' }),
}

export const FILTER_KEYS = Object.keys(
    filterCodec
) as (keyof typeof filterCodec)[]

export const defaultFilters = Object.fromEntries(
    FILTER_KEYS.map((k) => [k, filterCodec[k].default])
) as unknown as CandidateFilterState

export const areFiltersEqual = (
    a: CandidateFilterState,
    b: CandidateFilterState
): boolean =>
    FILTER_KEYS.every((key) => {
        const serialize = filterCodec[key].serialize as (
            val: unknown
        ) => string | null
        return (
            serialize((a as Record<string, unknown>)[key]) ===
            serialize((b as Record<string, unknown>)[key])
        )
    })
